/**
 * RemindersSection Component
 * Displays a horizontal row of growth reminder quote cards.
 * First card is always "Create Reminder", followed by up to 3 reminder cards.
 * User-created reminders show Edit/Delete; system defaults show a Save button.
 */

import { ArrowRight, Plus } from "lucide-react"
import { useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { toast } from "sonner"
import { Carousel } from "@/components/carousel/Carousel"
import { useTranslation } from "@/language"
import { getAssetUrl } from "@/utils/assets"
import { useGrowthReminders } from "../hooks/useGrowthReminders"
import { AddReminderModal } from "./AddReminderModal"
import { EditReminderModal } from "./EditReminderModal"
import { QuoteCard } from "./QuoteCard"

/**
 * Static background images for system default reminder cards.
 * Indexed by sortOrder: sortOrder 0 → image 0, sortOrder 1 → image 1, etc.
 * Each default always maps to the same image by its sortOrder,
 * regardless of which other defaults the user has saved.
 */
const DEFAULT_BG_IMAGES = [
  getAssetUrl("growth-reminder-1.png"),
  getAssetUrl("growth-reminder-2.png"),
  getAssetUrl("growth-reminder-3.png"),
]

const MAX_REMINDER_SLOTS = 3

type ReminderCarouselItem =
  | { type: "add" }
  | {
      type: "reminder"
      id: string
      text: string
      backgroundImageKey: string
      isSystemDefault: boolean
      sortOrder?: number
    }

export function RemindersSection() {
  const { t } = useTranslation("myRituality")
  const navigate = useNavigate()
  const { reminders, isLoading, deleteReminder, saveDefault, isSavingDefault } =
    useGrowthReminders()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingReminder, setEditingReminder] = useState<{ id: string; text: string } | null>(null)

  const userReminders = useMemo(() => reminders.filter(r => !r.isSystemDefault), [reminders])
  const shouldShowExpandedView = userReminders.length > 3

  const handleEdit = (id: string, text: string) => {
    setEditingReminder({ id, text })
  }

  const handleDelete = (id: string) => {
    deleteReminder(id, {
      onSuccess: () => {
        toast.success("Reminder deleted")
      },
      onError: () => {
        toast.error("Failed to delete reminder")
      },
    })
  }

  const handleSaveDefault = (id: string, backgroundImageKey: string) => {
    saveDefault(
      { defaultReminderId: id, backgroundImageKey },
      {
        onSuccess: () => {
          toast.success(t("reminders.saved"))
        },
        onError: () => {
          toast.error(t("reminders.saveFailed"))
        },
      },
    )
  }

  if (isLoading) {
    return (
      <section>
        <div className="h-6 w-64 bg-white/5 rounded animate-pulse mb-8" />
        <div className="flex gap-5 overflow-hidden">
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="w-[310px] h-[310px] shrink-0 rounded-tl-[40px] rounded-br-[40px] bg-white/5 animate-pulse"
            />
          ))}
        </div>
      </section>
    )
  }

  if (shouldShowExpandedView) {
    return (
      <section>
        {/* Section header with actions */}
        <div className="flex items-center justify-between mb-5 md:mb-8 px-5 md:px-0">
          <h2 className="text-lg md:text-2xl font-semibold leading-7.5 uppercase opacity-90 text-primary">
            {t("reminders.title")}
          </h2>
          <div className="hidden md:flex items-center gap-8">
            {/* Add Reminder button */}
            <button
              type="button"
              onClick={() => setIsModalOpen(true)}
              className="flex items-center gap-1.5 text-sm font-semibold text-primary hover:opacity-80 transition-opacity cursor-pointer"
            >
              <span className="p-1 bg-white rounded-full">
                <Plus className="w-4 h-4 text-[#000000]" />
              </span>
              <span>{t("reminders.addReminder")}</span>
            </button>
            {/* Show all link */}
            <button
              type="button"
              onClick={() => navigate("/my-rituality/reminders")}
              className="flex items-center gap-1.5 text-sm font-semibold text-primary hover:opacity-80 transition-opacity cursor-pointer"
            >
              <span>{t("reminders.showAll")}</span>
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Scrollable row of user reminder cards */}
        <Carousel
          data={userReminders}
          renderItem={r => (
            <QuoteCard
              text={r.text}
              backgroundImageKey={r.backgroundImageKey ?? "abstract-1"}
              showMenu={true}
              onEdit={() => handleEdit(r.id, r.text)}
              onDelete={() => handleDelete(r.id)}
            />
          )}
          itemWidth="310px"
          contentClassName="gap-5 touch-pan-x"
          opts={{ align: "start", loop: false }}
        />
        <div className="md:hidden flex items-center gap-8 mt-7.5 px-5">
          {/* Add Reminder button */}
          <button
            type="button"
            onClick={() => setIsModalOpen(true)}
            className="flex items-center gap-1.25 text-sm font-semibold text-primary hover:opacity-80 transition-opacity cursor-pointer"
          >
            <span className="p-1 bg-white rounded-full">
              <Plus className="w-3 h-3 text-[#000000]" />
            </span>
            <span>{t("reminders.addReminder")}</span>
          </button>
          {/* Show all link */}
          <button
            type="button"
            onClick={() => navigate("/my-rituality/reminders")}
            className="flex items-center gap-1.25 text-sm font-semibold text-primary hover:opacity-80 transition-opacity cursor-pointer"
          >
            <span>{t("reminders.showAll")}</span>
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>

        <AddReminderModal open={isModalOpen} onOpenChange={setIsModalOpen} />
        <EditReminderModal
          open={!!editingReminder}
          onOpenChange={open => !open && setEditingReminder(null)}
          reminderId={editingReminder?.id}
          defaultText={editingReminder?.text}
        />
      </section>
    )
  }

  return (
    <section>
      {/* Section header */}
      <div className="flex items-center justify-between mb-5 md:mb-8">
        <div className="flex flex-col gap-2.5 px-5">
          <h2 className="text-lg md:text-2xl font-semibold leading-[30px] uppercase opacity-90 text-primary">
            {t("reminders.title")}
          </h2>
          {reminders.length === 0 && (
            <p className="text-sm leading-6.25">
              Save a phrase, intention, or insight from your ritual.
            </p>
          )}
        </div>
      </div>

      {/* Quote cards row */}
      <ReminderCarousel
        reminders={reminders}
        onAdd={() => setIsModalOpen(true)}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onSaveDefault={handleSaveDefault}
        isSavingDefault={isSavingDefault}
        t={t}
      />

      <AddReminderModal open={isModalOpen} onOpenChange={setIsModalOpen} />
      <EditReminderModal
        open={!!editingReminder}
        onOpenChange={open => !open && setEditingReminder(null)}
        reminderId={editingReminder?.id}
        defaultText={editingReminder?.text}
      />
    </section>
  )
}

function ReminderCarousel({
  reminders,
  onAdd,
  onEdit,
  onDelete,
  onSaveDefault,
  isSavingDefault,
  t,
}: {
  reminders: Array<{
    id: string
    text: string
    backgroundImageKey?: string
    isSystemDefault?: boolean
    sortOrder?: number
  }>
  onAdd: () => void
  onEdit: (id: string, text: string) => void
  onDelete: (id: string) => void
  onSaveDefault: (id: string, backgroundImageKey: string) => void
  isSavingDefault: boolean
  t: (key: string) => string
}) {
  const carouselItems: ReminderCarouselItem[] = useMemo(() => {
    const userReminders = reminders.filter(r => !r.isSystemDefault)
    const systemDefaults = reminders.filter(r => r.isSystemDefault)
    const slotsRemaining = MAX_REMINDER_SLOTS - Math.min(userReminders.length, MAX_REMINDER_SLOTS)

    const items: ReminderCarouselItem[] = [{ type: "add" as const }]

    // Add user reminders (max 3)
    for (const r of userReminders.slice(0, MAX_REMINDER_SLOTS)) {
      items.push({
        type: "reminder",
        id: r.id,
        text: r.text,
        backgroundImageKey: r.backgroundImageKey ?? "abstract-1",
        isSystemDefault: false,
      })
    }

    // Fill remaining slots with system defaults (max 3 total reminders)
    // Use sortOrder to pick the image so each default keeps its own image
    for (let i = 0; i < Math.min(systemDefaults.length, slotsRemaining); i++) {
      const r = systemDefaults[i]
      const imgIndex = (r.sortOrder ?? i) % DEFAULT_BG_IMAGES.length
      items.push({
        type: "reminder",
        id: r.id,
        text: r.text,
        backgroundImageKey: DEFAULT_BG_IMAGES[imgIndex],
        isSystemDefault: true,
        sortOrder: r.sortOrder,
      })
    }

    return items
  }, [reminders])

  // Map each default reminder's sortOrder to its background image URL
  // This is stable regardless of which defaults have been saved
  const defaultImageMap = useMemo(() => {
    const map = new Map<string, string>()
    for (const r of reminders.filter(r => r.isSystemDefault)) {
      const imgIndex = (r.sortOrder ?? 0) % DEFAULT_BG_IMAGES.length
      map.set(r.id, DEFAULT_BG_IMAGES[imgIndex])
    }
    return map
  }, [reminders])

  return (
    <Carousel
      data={carouselItems}
      renderItem={item => {
        if (item.type === "add") {
          return (
            <div
              className="relative w-[310px] h-[310px] overflow-hidden rounded-tl-[40px] rounded-br-[40px] bg-[#0E0E0E] border border-dashed border-[#F2F0EB33] cursor-pointer group"
              onClick={onAdd}
            >
              <div className="absolute inset-0 flex flex-col items-center justify-center p-8">
                <div className="w-17.5 h-17.5 rounded-full border border-gradient-3d flex items-center justify-center mb-5 transition-colors">
                  <Plus className="w-12 h-12 text-[#F2F0EB]" />
                </div>
                <p className="text-[14px] font-semibold text-white text-center uppercase">
                  {t("reminders.createReminder")}
                </p>
              </div>
            </div>
          )
        }

        if (item.isSystemDefault) {
          const bgUrl = defaultImageMap.get(item.id) ?? DEFAULT_BG_IMAGES[0]
          return (
            <QuoteCard
              text={item.text}
              backgroundImageKey={bgUrl}
              showMenu={false}
              onSave={() => onSaveDefault(item.id, bgUrl)}
              isSaving={isSavingDefault}
            />
          )
        }

        return (
          <QuoteCard
            text={item.text}
            backgroundImageKey={item.backgroundImageKey}
            showMenu={true}
            onEdit={() => onEdit(item.id, item.text)}
            onDelete={() => onDelete(item.id)}
          />
        )
      }}
      itemWidth="310px"
      contentClassName="gap-5 touch-pan-x"
      opts={{ align: "start", loop: false }}
    />
  )
}
